/**
 * PHARMAKON — Trésorerie · L1, the drawer stack (TWP-20; design/ux/03_reglements_journaux_tiers_dettes.md
 * §11.1, 02_ARCHITECTURE §8.2, 03_UX_DESIGN §2.2).
 *
 * A drawer is addressed by `?panneau=<objet>:<id>` on whatever page is open (an optional
 * `&onglet=<tab>` names the tab it opens on). A link inside a drawer PUSHES a new panneau (a browser
 * history entry); the panneaux under the visible one travel in the history entry's state (`pile`), so
 * Back pops one level by itself and the header can show the mini-breadcrumb of the stack (at most
 * `FIL_PANNEAUX_MAX` crumbs, « … » for older ones). Échap closes the whole stack in one gesture. Only
 * one drawer is visible at a time: the stack lives in the history, never piled on screen.
 *
 * Pure (no DOM, no router): `hooks/usePanneauxTresorerie.js` binds it to react-router.
 */

/** The query parameters of a drawer (03_UX_DESIGN §2.2). */
export const PARAM_PANNEAU = "panneau";
export const PARAM_ONGLET_PANNEAU = "onglet";
const SEPARATEUR = ":";

/**
 * Every drawer object of the URL grammar (03_UX_DESIGN §2.2; the server builds the same links —
 * `OBJETS_PANNEAU_TRESORERIE`, `config/tresorerie-pharmacie.config.js`).
 */
export const OBJETS_PANNEAU = Object.freeze({
  COMPTE: "compte",
  SESSION: "session",
  OPERATION: "operation",
  FACTURE: "facture",
  TIERS: "tiers",
  CHARGE: "charge",
  RECURRENCE: "recurrence",
  SIGNAL: "signal",
  COMPTAGE: "comptage",
});
const OBJETS = new Set(Object.values(OBJETS_PANNEAU));

/**
 * The drawers the client renders today (the others arrive with their cards: facture / tiers TWP-26,
 * charge TWP-27, récurrence TWP-31, signal / comptage TWP-39). A link to an object not hosted yet is
 * shown without a link (its number and words stay readable) — never a dead drawer.
 */
export const OBJETS_PANNEAU_HEBERGES = Object.freeze([
  OBJETS_PANNEAU.COMPTE,
  OBJETS_PANNEAU.SESSION,
  OBJETS_PANNEAU.OPERATION,
  // TWP-26 — the facture drawer (G6) and the fournisseur's drawer (T2), hosted by every page that hosts
  // the operation drawer (`PanneauxReglements`).
  OBJETS_PANNEAU.FACTURE,
  OBJETS_PANNEAU.TIERS,
  // TWP-27 — the charge drawer (UX 03 §13.5), hosted with them (`PanneauxReglements` → `PanneauxCharges`).
  OBJETS_PANNEAU.CHARGE,
]);

/** At most this many crumbs in a drawer's mini-breadcrumb (UX 03 §11.1); older ones read « … ». */
export const FIL_PANNEAUX_MAX = 4;

/** `?panneau=<objet>:<id>` → `{ objet, id }` (null when absent, malformed or an unknown object). */
export function lirePanneau(valeur) {
  if (typeof valeur !== "string") return null;
  const i = valeur.indexOf(SEPARATEUR);
  if (i <= 0 || i === valeur.length - 1) return null;
  const objet = valeur.slice(0, i);
  const id = valeur.slice(i + 1);
  return OBJETS.has(objet) ? { objet, id } : null;
}

export const valeurPanneau = (objet, id) => `${objet}${SEPARATEUR}${id}`;

/** Is this object's drawer rendered by the client today? */
export const panneauHeberge = (objet) => OBJETS_PANNEAU_HEBERGES.includes(objet);

/**
 * A server-built route (`document_source.route`, A-UX3-11) read as a drawer of the current page when it
 * carries `panneau=` — the drawer opens over the page the user is on (UX 03 §11.1), never on another
 * leaf. Returns `{ objet, id, onglet }` or null (a route to another page).
 */
export function panneauDeRoute(route) {
  if (typeof route !== "string" || !route.includes(`${PARAM_PANNEAU}=`)) return null;
  const q = route.includes("?") ? route.slice(route.indexOf("?") + 1) : route;
  const p = new URLSearchParams(q);
  const lu = lirePanneau(p.get(PARAM_PANNEAU));
  return lu ? { ...lu, onglet: p.get(PARAM_ONGLET_PANNEAU) || null } : null;
}

/**
 * The search string of the page with the drawer parameters set (`panneau` = null removes the drawer
 * and its tab). Every other parameter (filters, view, focus) is kept.
 * @param {string|URLSearchParams} search
 * @param {{ panneau: string|null, onglet?: string|null }} cible
 */
export function searchAvecPanneau(search, { panneau, onglet = null }) {
  const p = new URLSearchParams(typeof search === "string" ? search.replace(/^\?/, "") : search);
  if (panneau) p.set(PARAM_PANNEAU, panneau);
  else p.delete(PARAM_PANNEAU);
  if (panneau && onglet) p.set(PARAM_ONGLET_PANNEAU, onglet);
  else p.delete(PARAM_ONGLET_PANNEAU);
  const s = p.toString();
  return s ? `?${s}` : "";
}

/**
 * The history state of the entry a push creates: the panneaux under it (`pile`, oldest first, each
 * `{ valeur, libelle }`) and `depuisPage` (true when the first drawer of the stack was opened from the
 * page itself — then Échap goes back to that page entry instead of adding one).
 * @param {{ pile?: Array<{valeur: string, libelle?: string}>, depuisPage?: boolean } | null} etat the current entry's state
 * @param {{ valeur: string, libelle?: string } | null} courant the drawer visible now (null: none)
 */
export function etatApresEmpilement(etat, courant) {
  const pile = Array.isArray(etat?.pile) ? etat.pile : [];
  if (!courant) return { pile: [], depuisPage: true };
  // A stack started by a deep link (no state on its entry) has no page entry under it.
  return { pile: [...pile, { valeur: courant.valeur, libelle: courant.libelle ?? null }], depuisPage: etat?.depuisPage === true };
}

/**
 * How Échap closes the whole stack: `{ retour: n }` goes n entries back (the drawers were pushed from
 * the page in this tab), else `{ remplacer: true }` (a deep link opened in a new tab: no page entry
 * under it — the drawer parameters are removed in place).
 */
export function fermetureDeLaPile(etat) {
  const pile = Array.isArray(etat?.pile) ? etat.pile : [];
  return etat?.depuisPage === true ? { retour: pile.length + 1 } : { remplacer: true };
}

/**
 * The mini-breadcrumb of the stack: the last crumbs (the visible drawer last), « … » when older
 * ones are hidden. Each crumb `{ valeur, libelle, profondeur }` — `profondeur` = how many entries back.
 */
export function filPanneaux(pile, courant, max = FIL_PANNEAUX_MAX) {
  const tous = [...(Array.isArray(pile) ? pile : []), ...(courant ? [courant] : [])];
  if (tous.length <= 1) return { crumbs: tous.map((c) => ({ ...c, profondeur: 0 })), tronque: false };
  const debut = Math.max(0, tous.length - max);
  return {
    crumbs: tous.slice(debut).map((c, i, l) => ({ ...c, profondeur: l.length - 1 - i })),
    tronque: debut > 0,
  };
}
